import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";

const actionName = actionNameBuilder("featureInfo");

/** Attributes of a single vector tile feature, exactly as they were baked into the tiles. */
export type FeatureAttributes = Record<string, unknown>;

/**
 * Attributes of the vector tile features under the last map click ("feature info",
 * the equivalent of QGIS identify).
 *
 * These features are shapes inside a PMTiles upload, not API entities, so they have
 * no id to select and nothing to fetch — the click event carries everything we can
 * show. This store holds WHAT is being inspected; popupStore ("feature-info" type)
 * holds whether the popup is open and where it is anchored.
 */
interface FeatureInfoState {
  /** Every feature under the click point, topmost first. Empty when nothing is being inspected. */
  features: FeatureAttributes[];
  /** Which of `features` the popup is currently showing. */
  activeIndex: number;
  /** Dataset/layer name, used as the popup title. */
  sourceName: string | null;

  actions: {
    /** Replace the inspected features (a new click always starts at the topmost one). */
    setFeatures: (features: FeatureAttributes[], sourceName?: string | null) => void;
    /** Move through overlapping features, wrapping at either end. */
    stepFeature: (delta: number) => void;
    /** Drop the inspected features — pair with popupStore.closePopup. */
    clear: () => void;
    reset: () => void;
  };
}

const initialState = {
  features: [] as FeatureAttributes[],
  activeIndex: 0,
  sourceName: null as string | null
};

export const useFeatureInfoStore = create<FeatureInfoState>()(
  devtools(
    immer(
      applyResetMiddleware((set, _, store) => ({
        ...initialState,
        actions: {
          setFeatures: (features, sourceName) =>
            set(
              (state) => {
                state.features = features;
                state.activeIndex = 0;
                state.sourceName = sourceName ?? null;
              },
              false,
              actionName("setFeatures")
            ),

          stepFeature: (delta) =>
            set(
              (state) => {
                const count = state.features.length;
                if (count === 0) return;
                state.activeIndex = (state.activeIndex + delta + count) % count;
              },
              false,
              actionName("stepFeature")
            ),

          clear: () =>
            set(
              (state) => {
                state.features = [];
                state.activeIndex = 0;
                state.sourceName = null;
              },
              false,
              actionName("clear")
            ),

          reset: () => set(store.getInitialState(), false, actionName("reset"))
        }
      }))
    ),
    { name: "featureInfoStore" }
  )
);

export const useFeatureInfoSelector = () =>
  useFeatureInfoStore(
    useShallow((state) => ({
      features: state.features,
      activeIndex: state.activeIndex,
      sourceName: state.sourceName
    }))
  );

export const useFeatureInfoActions = () => useFeatureInfoStore((state) => state.actions);
